import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserRoundCog, Ear } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import UpcomingEvents from "@/components/UpcomingEvents";
import PushOptIn from "@/components/PushOptIn";
import AttendanceTodayCard from "@/components/AttendanceTodayCard";
import MemberAttendanceCard from "@/components/MemberAttendanceCard";
import VolunteerBadge from "@/components/VolunteerBadge";
import VolunteerTodayCard from "@/components/VolunteerTodayCard";
import ShowNotesBadge from "@/components/shownotes/ShowNotesBadge";
import MemberStatBadges from "@/components/MemberStatBadges";
import BioPromptCard from "@/components/bios/BioPromptCard";
import PageHeader, { schoolYear } from "@/components/PageHeader";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/lib/featureFlags";
import DashboardNotificationsCard from "@/components/notifications/DashboardNotificationsCard";

export default function MemberDashboard() {
  const { user } = useAuth();
  const { phonebook: phonebookOn, schedule: scheduleOn, attendance: attendanceOn, volunteers: volunteersOn, thespian: thespianOn, merit_tracking: meritOn, lettering: letteringOn, show_notes: showNotesOn } = useFeatureFlags();
  const [d, setD] = useState(null);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => { api.get("/member/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!phonebookOn) { setShowReminder(false); return; }
    const fields = [user.member_phone, user.guardian_first_name, user.guardian_last_name, user.guardian_phone, user.guardian_email];
    const incomplete = fields.some((v) => !v || !String(v).trim());
    const token = localStorage.getItem("rallypointgo_token") || "";
    const dismissedForToken = sessionStorage.getItem("profile-reminder-dismissed-token");
    setShowReminder(incomplete && dismissedForToken !== token && !!user.terms_accepted_at);
  }, [phonebookOn, user.member_phone, user.guardian_first_name, user.guardian_last_name, user.guardian_phone, user.guardian_email, user.terms_accepted_at]);

  const dismissReminder = () => {
    sessionStorage.setItem("profile-reminder-dismissed-token", localStorage.getItem("rallypointgo_token") || "");
    setShowReminder(false);
  };

  return (
    <div>
      <Dialog open={showReminder} onOpenChange={(o) => { if (!o) dismissReminder(); }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="profile-reminder-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-100 text-sky-600">
              <UserRoundCog className="h-6 w-6" />
            </div>
            <DialogTitle className="text-center font-heading">Complete your profile</DialogTitle>
            <DialogDescription className="text-center">
              Please add your phone number and a parent/guardian emergency contact so your coach can reach someone if needed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <Button variant="outline" className="rounded-full" onClick={dismissReminder} data-testid="profile-reminder-later-btn">Later</Button>
            <Link to="/member/profile?section=contact" onClick={dismissReminder}>
              <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="profile-reminder-complete-btn">Complete now</Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PageHeader
        title={`Welcome, ${user.first_name}`}
        subtitle={d ? `${[d.current_campus, d.current_team || "Team"].filter(Boolean).join(" ")} · Active year ${schoolYear(d.active_year)}` : "Loading your stats…"}
        actions={(volunteersOn || showNotesOn) && (
          <div className="flex flex-wrap items-center gap-2">
            {volunteersOn && <VolunteerBadge />}
            {showNotesOn && <ShowNotesBadge />}
          </div>
        )}
      />
      <PushOptIn />

      {user.can_announce && (
        <Link to="/member/announcements" className="mb-4 block" data-testid="dashboard-announcement-link">
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="dashboard-announcement-btn">
            <Ear className="mr-2 h-4 w-4" /> Draft a Rally Call
          </Button>
        </Link>
      )}
      {volunteersOn && <VolunteerTodayCard />}
      {attendanceOn && <AttendanceTodayCard testId="member-attendance-today-card" />}
      <BioPromptCard />

      <MemberStatBadges d={d} meritOn={meritOn} letteringOn={letteringOn} thespianOn={thespianOn} />
      {attendanceOn && <MemberAttendanceCard />}
      {scheduleOn && <UpcomingEvents endpoint="/member/schedule" to="/member/schedule" />}

      <DashboardNotificationsCard />
    </div>
  );
}
